import { useState, useEffect } from "react";
import SearchPage from "./components/SearchPage";
import CertificateDetail from "./components/DetailSingleDegree";
import Real3DFlipbook from "./components/Real3DFlipbook";
import LoginForm from "./components/LoginForm";
import MicrosoftCallback from "./components/MicrosoftCallback";
import PublicLayout from "./components/layouts/PublicLayout";
import AdminPage from "./components/layouts/AdminLayout";
import { useAuthStore } from "./lib/stores/auth";

import { publicSearchApi } from "./lib";
import { SearchObjectType, SearchResult } from "./lib/api/public-search";
import { Button } from "./components/ui/button";
import { useViewStore } from "./lib/stores/view";

type PublicSection = "search" | "template-2023" | "template-bachelor" | "template-master";
export type AdminView = "dashboard" | "users" | "bachelor-degrees" | "master-degrees" | "language-certificates" | "awards" | "scholarship" | "scholarship-title";

export default function App() {
	const params = new URLSearchParams(window.location.search);
	const degreeType = params.get("type") as SearchObjectType;
	const degreeId = params.get("id");
	const studentId = params.get("mssv");
	const code = params.get("code");
	const error = params.get("error");
	const { currentView, setCurrentView } = useViewStore();

	const { clearAuth } = useAuthStore();
	const [currentSection, setCurrentSection] = useState<PublicSection>("search");

	const [selectedCertificate, setSelectedCertificate] = useState<SearchResult | null>(null);
	const [showFlipbook, setShowFlipbook] = useState<"2023" | "bachelor" | "master" | null>(null);

	useEffect(() => {
		// Ưu tiên 1: Xử lý Microsoft callback
		if (code) {
			setCurrentView("microsoft-callback");
			return;
		}
		if (error === "access_denied") {
			setCurrentView("access-denied");
			return;
		}

		// Ưu tiên 2: Xử lý certificate detail params
		if (degreeType !== null && degreeId !== null) {
			setCurrentView("certificate-detail");
			(async () => {
				const res = await publicSearchApi.getDetail({ id: degreeId, type: degreeType });
				if (res.success) {
					setSelectedCertificate(res.data);
				}
			})();
		} else if (studentId !== null) {
			//= Dành cho cấu trúc tìm kiếm của trang cũ
			setCurrentView("certificate-detail");
			(async () => {
				const res = await publicSearchApi.getDetail({ id: studentId, type: "mssv" });
				if (res.success) {
					setSelectedCertificate(res.data);
				}
			})();
		}
		// Ưu tiên 3: Dùng currentView từ store (đã được restore từ localStorage)
	}, [code, error, degreeType, degreeId, studentId, setCurrentView]);

	function setOriginUrl() {
		window.history.replaceState({}, "", "/");
	}

	useEffect(() => {
		// Clean up URL if it's an admin callback
		if (["microsoft-callback", "access-denied"].includes(currentView) && window.location.pathname === import.meta.env.VITE_REACT_APP_MICROSOFT_REDIRECT_URI) {
			setOriginUrl()
		}
	}, [currentView]);

	const handleAdminLogin = () => {
		setCurrentView("admin");
	};
	const moveToLoginPage = () => {
		setCurrentView("admin-login");
	};

	const logout = () => {
		clearAuth();
		setCurrentView("search");
		setOriginUrl();
	};


	const handleBackToSearch = () => {
		setCurrentView("search");
		setSelectedCertificate(null);
		setOriginUrl();
	};

	const handleNavigate = (section: string) => {
		if (section === "search") {
			setCurrentSection("search");
			setCurrentView("search");
			setShowFlipbook(null); // Clear flipbook
		} else if (section === "template-2023") {
			setCurrentSection("template-2023");
			setShowFlipbook("2023");
		} else if (section === "template-bachelor") {
			setCurrentSection("template-bachelor");
			setShowFlipbook("bachelor");
		} else if (section === "template-master") {
			setCurrentSection("template-master");
			setShowFlipbook("master");
		}
	};

	const handleCloseFlipbook = () => {
		setShowFlipbook(null);
		setCurrentSection("search");
	};

	switch (currentView) {
		case "access-denied":
			//= Màn hình không có quyền truy cập
			return <div className="grid place-items-center w-screen h-screen">
				<div className="flex flex-col items-center gap-6 p-6 rounded-md border border-border">
					<div>Bạn không có quyền truy cập</div>
					<div>
						<Button type="button" variant={"add"} onClick={logout}>Quay lại trang tra cứu</Button>
					</div>
				</div>
			</div>
		case "certificate-detail":
			//= Màn hình chi tiết 1 văn bằng
			return <PublicLayout
				onNavigate={handleNavigate}
				currentSection={currentSection}
			>
				<div className='w-full'>
					<div className='max-w-7xl mx-auto p-6 space-y-3'>
						<CertificateDetail data={selectedCertificate} onBack={handleBackToSearch} />
					</div>
				</div>
			</PublicLayout>
		case "admin-login":
			//= Màn hình đăng nhập admin
			return <LoginForm />
		case "microsoft-callback":
			//= Màn hình chờ xử lý callback từ Microsoft
			return <MicrosoftCallback onSuccess={handleAdminLogin} onError={moveToLoginPage} onAccessDenied={() => setCurrentView("access-denied")} />
		case "admin":
			//= Màn hình quản trị
			return <AdminPage logout={logout} />;
		default:
			//= Màn hình tra cứu văn bằng
			return <PublicLayout onAdminAccess={() => setCurrentView("admin-login")} onNavigate={handleNavigate} currentSection={currentSection}>
				<div className='w-full'>
					<SearchPage />
				</div>
				{showFlipbook && <Real3DFlipbook type={showFlipbook} onClose={handleCloseFlipbook} />}
			</PublicLayout>
	}


}
